import { Workflow } from '@/stores/workflowStore';
import { AVERAGE_USER_TEMPLATES } from '@/services/averageUserTemplates';

export const WORKFLOW_TEMPLATES: Record<string, Workflow & { difficulty: string; category: string; features: string[]; useCase?: string }> = {
  // TEMPLATE 1: SIMPLE - Email Alert on Webhook (Beginner)
  simple_email_alert: {
    id: 'template-001',
    name: '🔔 Email Alert on Event',
    difficulty: 'Beginner',
    category: 'Notifications',
    description: 'Send an email notification when a webhook event is received. Perfect for getting started with webhooks and notifications.',
    features: ['Webhook Trigger', 'Email Action', 'Data Transformation', 'Real-time notifications'],
    nodes: [
      {
        id: 'webhook-trigger',
        type: 'trigger',
        subtype: 'webhook',
        config: {
          name: 'Webhook Trigger',
          url: 'https://your-domain.com/webhook/event',
          description: 'Receives incoming webhook events'
        },
        x: 50,
        y: 150,
      },
      {
        id: 'transform-data',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Format Email',
          transform: `return {
  recipient: input.email || 'user@example.com',
  subject: 'Event Alert: ' + (input.eventType || 'Unknown'),
  body: 'Event received at ' + new Date().toLocaleString() + '\\nDetails: ' + JSON.stringify(input, null, 2)
}`
        },
        x: 300,
        y: 150,
      },
      {
        id: 'send-email',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Send Email',
          type: 'email',
          message: 'Event notification sent'
        },
        x: 550,
        y: 150,
      },
      {
        id: 'log-action',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Log Activity',
          message: 'Email notification sent successfully'
        },
        x: 800,
        y: 150,
      },
    ],
    edges: [
      { id: 'edge-1', from: 'webhook-trigger', to: 'transform-data' },
      { id: 'edge-2', from: 'transform-data', to: 'send-email' },
      { id: 'edge-3', from: 'send-email', to: 'log-action' },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },

  // TEMPLATE 2: INTERMEDIATE - Data Validation & Conditional Routing (Advanced Beginner)
  intermediate_data_validation: {
    id: 'template-002',
    name: '✅ Data Validation & Routing',
    difficulty: 'Intermediate',
    category: 'Data Processing',
    description: 'Validate incoming data and route it to different actions based on validation results. Learn conditionals and error handling.',
    features: ['Conditional Logic', 'Data Validation', 'Error Handling', 'Multiple Paths', 'Logging'],
    nodes: [
      {
        id: 'api-trigger',
        type: 'trigger',
        subtype: 'webhook',
        config: {
          name: 'Receive Data',
          url: 'https://your-api.com/webhook/data',
          description: 'Incoming data from external source'
        },
        x: 50,
        y: 200,
      },
      {
        id: 'validate-data',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Validate Input',
          transform: `const data = input;
const isValid = data.email && data.name && data.age > 0;
return {
  ...data,
  valid: isValid,
  errors: !isValid ? ['Missing required fields'] : [],
  timestamp: new Date().toISOString()
}`
        },
        x: 300,
        y: 200,
      },
      {
        id: 'check-valid',
        type: 'tool',
        subtype: 'conditional',
        config: {
          name: 'Is Data Valid?',
          condition: 'input.valid === true'
        },
        x: 550,
        y: 200,
      },
      {
        id: 'store-valid',
        type: 'action',
        subtype: 'storage',
        config: {
          name: 'Store Valid Data',
          key: 'valid_records',
          description: 'Save valid records to storage'
        },
        x: 800,
        y: 100,
      },
      {
        id: 'notify-error',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Alert Invalid Data',
          type: 'error',
          message: 'Data validation failed'
        },
        x: 800,
        y: 300,
      },
      {
        id: 'log-valid',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Log Success',
          message: 'Data validated and stored'
        },
        x: 1050,
        y: 100,
      },
      {
        id: 'log-error',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Log Error',
          message: 'Validation failed'
        },
        x: 1050,
        y: 300,
      },
    ],
    edges: [
      { id: 'edge-1', from: 'api-trigger', to: 'validate-data' },
      { id: 'edge-2', from: 'validate-data', to: 'check-valid' },
      { id: 'edge-3', from: 'check-valid', to: 'store-valid' },
      { id: 'edge-4', from: 'check-valid', to: 'notify-error' },
      { id: 'edge-5', from: 'store-valid', to: 'log-valid' },
      { id: 'edge-6', from: 'notify-error', to: 'log-error' },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },

  // TEMPLATE 3: ADVANCED - Multi-Step API Orchestration (Advanced)
  advanced_api_orchestration: {
    id: 'template-003',
    name: '🚀 Multi-Step API Workflow',
    difficulty: 'Advanced',
    category: 'API Integration',
    description: 'Orchestrate multiple API calls with data transformation, error handling, and conditional branching. Demonstrates advanced integrations.',
    features: ['Multiple HTTP Calls', 'Data Transformation', 'Conditional Logic', 'Error Recovery', 'API Orchestration', 'Performance Logging'],
    nodes: [
      {
        id: 'schedule-trigger',
        type: 'trigger',
        subtype: 'timer',
        config: {
          name: 'Scheduled Run',
          interval: 3600,
          description: 'Runs every hour'
        },
        x: 50,
        y: 250,
      },
      {
        id: 'fetch-users',
        type: 'tool',
        subtype: 'http',
        config: {
          name: 'Fetch Users API',
          method: 'GET',
          url: 'https://jsonplaceholder.typicode.com/users',
          headers: { 'Content-Type': 'application/json' }
        },
        x: 300,
        y: 150,
      },
      {
        id: 'transform-users',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Transform Data',
          transform: `const users = Array.isArray(input) ? input : [];
return {
  users: users.map(u => ({
    id: u.id,
    name: u.name,
    email: u.email,
    processed: true
  })),
  count: users.length,
  processedAt: new Date().toISOString()
}`
        },
        x: 550,
        y: 150,
      },
      {
        id: 'check-users',
        type: 'tool',
        subtype: 'conditional',
        config: {
          name: 'Users Found?',
          condition: 'input.count > 0'
        },
        x: 800,
        y: 150,
      },
      {
        id: 'batch-process',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Batch Process',
          transform: `return {
  batchId: Date.now(),
  records: input.users.slice(0, 5),
  total: input.count,
  timestamp: new Date().toISOString()
}`
        },
        x: 1050,
        y: 100,
      },
      {
        id: 'post-results',
        type: 'tool',
        subtype: 'http',
        config: {
          name: 'Send Results',
          method: 'POST',
          url: 'https://api.example.com/results',
          description: 'Post processed results'
        },
        x: 1300,
        y: 100,
      },
      {
        id: 'store-results',
        type: 'action',
        subtype: 'storage',
        config: {
          name: 'Store Results',
          key: 'batch_results'
        },
        x: 1550,
        y: 100,
      },
      {
        id: 'log-success',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Log Success',
          message: 'Batch processing completed'
        },
        x: 1800,
        y: 100,
      },
      {
        id: 'notify-error',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Alert No Data',
          type: 'warning',
          message: 'No users found'
        },
        x: 1050,
        y: 250,
      },
      {
        id: 'log-error',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Log Error',
          message: 'No data to process'
        },
        x: 1300,
        y: 250,
      },
    ],
    edges: [
      { id: 'edge-1', from: 'schedule-trigger', to: 'fetch-users' },
      { id: 'edge-2', from: 'fetch-users', to: 'transform-users' },
      { id: 'edge-3', from: 'transform-users', to: 'check-users' },
      { id: 'edge-4', from: 'check-users', to: 'batch-process' },
      { id: 'edge-5', from: 'batch-process', to: 'post-results' },
      { id: 'edge-6', from: 'post-results', to: 'store-results' },
      { id: 'edge-7', from: 'store-results', to: 'log-success' },
      { id: 'edge-8', from: 'check-users', to: 'notify-error' },
      { id: 'edge-9', from: 'notify-error', to: 'log-error' },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },

  // TEMPLATE 4: EXPERT - Enterprise Data Pipeline with Integrations (Expert)
  expert_data_pipeline: {
    id: 'template-004',
    name: '⚡ Enterprise Data Pipeline',
    difficulty: 'Expert',
    category: 'Enterprise Integration',
    description: 'Complete enterprise-grade data pipeline with API orchestration, Slack notifications, data transformation, error recovery, and analytics. Showcases all system capabilities.',
    features: ['Multiple Triggers', 'API Orchestration', 'Slack Integration', 'Data Aggregation', 'Error Recovery', 'Performance Analytics', 'Data Persistence', 'Advanced Conditional Logic'],
    nodes: [
      {
        id: 'webhook-entry',
        type: 'trigger',
        subtype: 'webhook',
        config: {
          name: 'Data Entry Point',
          url: 'https://enterprise.example.com/webhook/process',
          description: 'Main data ingestion endpoint'
        },
        x: 50,
        y: 300,
      },
      {
        id: 'validate-schema',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Schema Validation',
          transform: `const data = input;
const required = ['id', 'source', 'payload'];
const isValid = required.every(f => f in data);
return {
  ...data,
  validated: isValid,
  validationErrors: !isValid ? required.filter(f => !(f in data)) : [],
  validatedAt: new Date().toISOString()
}`
        },
        x: 300,
        y: 300,
      },
      {
        id: 'quality-check',
        type: 'tool',
        subtype: 'conditional',
        config: {
          name: 'Data Quality Check',
          condition: 'input.validated && input.payload && Object.keys(input.payload).length > 0'
        },
        x: 550,
        y: 300,
      },
      {
        id: 'enrich-data',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Data Enrichment',
          transform: `return {
  original: input,
  enriched: {
    ...input.payload,
    source: input.source,
    ingestionTime: new Date().toISOString(),
    recordId: 'REC-' + Date.now(),
    version: '1.0'
  },
  metadata: {
    processedAt: new Date().toISOString(),
    processorVersion: '2.1.0',
    dataQuality: 'high'
  }
}`
        },
        x: 800,
        y: 200,
      },
      {
        id: 'store-primary',
        type: 'action',
        subtype: 'storage',
        config: {
          name: 'Store in Primary',
          key: 'enterprise_records'
        },
        x: 1050,
        y: 150,
      },
      {
        id: 'post-database',
        type: 'tool',
        subtype: 'http',
        config: {
          name: 'Post to Database',
          method: 'POST',
          url: 'https://database.example.com/api/records',
          description: 'Persist to main database'
        },
        x: 1300,
        y: 150,
      },
      {
        id: 'notify-slack-success',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Slack Notification',
          type: 'slack',
          channel: '#data-pipeline',
          message: 'Record processed successfully'
        },
        x: 1550,
        y: 150,
      },
      {
        id: 'log-success',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Log Success Metrics',
          message: 'Enterprise pipeline completed'
        },
        x: 1800,
        y: 150,
      },
      {
        id: 'handle-error',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Error Handling',
          transform: `return {
  error: true,
  originalData: input,
  errors: input.validationErrors || [],
  errorTime: new Date().toISOString(),
  action: 'Review required'
}`
        },
        x: 800,
        y: 400,
      },
      {
        id: 'store-errors',
        type: 'action',
        subtype: 'storage',
        config: {
          name: 'Archive Errors',
          key: 'pipeline_errors'
        },
        x: 1050,
        y: 400,
      },
      {
        id: 'notify-slack-error',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Alert Team',
          type: 'slack',
          channel: '#alerts',
          message: 'Pipeline error detected - review required'
        },
        x: 1300,
        y: 400,
      },
      {
        id: 'log-error',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Log Error Details',
          message: 'Pipeline error recorded'
        },
        x: 1550,
        y: 400,
      },
      {
        id: 'analytics-timer',
        type: 'trigger',
        subtype: 'timer',
        config: {
          name: 'Analytics Schedule',
          interval: 86400,
          description: 'Daily analytics run'
        },
        x: 50,
        y: 600,
      },
      {
        id: 'fetch-metrics',
        type: 'tool',
        subtype: 'http',
        config: {
          name: 'Fetch Metrics',
          method: 'GET',
          url: 'https://analytics.example.com/api/metrics'
        },
        x: 300,
        y: 600,
      },
      {
        id: 'aggregate-stats',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Aggregate Statistics',
          transform: `const metrics = Array.isArray(input) ? input : [];
return {
  totalRecords: metrics.reduce((s, m) => s + (m.count || 0), 0),
  successRate: (metrics.filter(m => m.status === 'success').length / metrics.length * 100).toFixed(2),
  errorCount: metrics.filter(m => m.status === 'error').length,
  generatedAt: new Date().toISOString(),
  period: 'daily'
}`
        },
        x: 550,
        y: 600,
      },
      {
        id: 'store-analytics',
        type: 'action',
        subtype: 'storage',
        config: {
          name: 'Store Analytics',
          key: 'daily_analytics'
        },
        x: 800,
        y: 600,
      },
      {
        id: 'notify-report',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Send Report',
          type: 'slack',
          channel: '#reports',
          message: 'Daily analytics report generated'
        },
        x: 1050,
        y: 600,
      },
    ],
    edges: [
      { id: 'edge-1', from: 'webhook-entry', to: 'validate-schema' },
      { id: 'edge-2', from: 'validate-schema', to: 'quality-check' },
      { id: 'edge-3', from: 'quality-check', to: 'enrich-data' },
      { id: 'edge-4', from: 'enrich-data', to: 'store-primary' },
      { id: 'edge-5', from: 'store-primary', to: 'post-database' },
      { id: 'edge-6', from: 'post-database', to: 'notify-slack-success' },
      { id: 'edge-7', from: 'notify-slack-success', to: 'log-success' },
      { id: 'edge-8', from: 'quality-check', to: 'handle-error' },
      { id: 'edge-9', from: 'handle-error', to: 'store-errors' },
      { id: 'edge-10', from: 'store-errors', to: 'notify-slack-error' },
      { id: 'edge-11', from: 'notify-slack-error', to: 'log-error' },
      { id: 'edge-12', from: 'analytics-timer', to: 'fetch-metrics' },
      { id: 'edge-13', from: 'fetch-metrics', to: 'aggregate-stats' },
      { id: 'edge-14', from: 'aggregate-stats', to: 'store-analytics' },
      { id: 'edge-15', from: 'store-analytics', to: 'notify-report' },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },

  ...AVERAGE_USER_TEMPLATES,
};

export function getTemplateById(id: string): Workflow | undefined {
  return Object.values(WORKFLOW_TEMPLATES).find((t) => t.id === id);
}

export function getAllTemplates(): Workflow[] {
  return Object.values(WORKFLOW_TEMPLATES);
}

export function getTemplateByName(name: string): Workflow | undefined {
  return Object.values(WORKFLOW_TEMPLATES).find((t) => t.name === name);
}